/**
 * Pure field-selection + confirm helpers for the wrap-up digest review
 * (88 · Task 2.3).
 *
 * The review screen shows each public-safe field with an editable value and an
 * independent "publish to profile?" toggle. Publishing to the investor-visible
 * `startup_profiles` row is a HARD-gated action: a field reaches the DB write
 * ONLY when its toggle is on AND its key is in the {@link PUBLIC_SAFE_KEYS}
 * allowlist (the write seam `publishPublicSafeFields` re-applies the same
 * allowlist server-adjacent, so this is defence-in-depth, not the only guard).
 *
 * Crucially, publish and RESCORE are decoupled (spec §1): whatever the founder
 * toggles — or if they toggle nothing at all — the rescore path (`onConfirmed`)
 * always runs. Declining to publish must never skip the rescore.
 *
 * Kept as standalone pure functions (no React / React-Native imports) so the
 * publish-payload contract is unit-tested without rendering, matching the
 * pure-helper style of `interview-exit.ts` / `voice-button-state.ts`.
 */

import { PUBLIC_SAFE_KEYS } from '@/lib/interview/publish-profile';

/** One editable public-safe field row in the review screen. */
export interface DigestFieldState {
  /** The `startup_profiles` column this field maps to (e.g. `about`, `tagline`). */
  key: string;
  /** The founder-editable value (seeded from the digest, correctable before publish). */
  value: string;
  /** Whether the founder has approved publishing this field to the public profile. */
  publish: boolean;
}

/**
 * Build the publish payload = (toggled-on ∩ allowlist ∩ non-empty).
 *
 * A field is included ONLY if its toggle is on, its key is allowlisted, and its
 * trimmed value is non-empty (an approved-but-blank field would otherwise wipe a
 * profile column — never publish emptiness). Any sensitive / non-allowlisted key
 * is dropped here even if it somehow carried a toggle.
 */
export function selectPublishFields(
  fieldStates: DigestFieldState[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const field of fieldStates) {
    if (!field.publish) continue;
    if (!PUBLIC_SAFE_KEYS.includes(field.key)) continue;
    const value = field.value.trim();
    if (value.length === 0) continue;
    out[field.key] = value;
  }
  return out;
}

/** Injected publish seam — mirrors `publishPublicSafeFields`' return shape. */
export type PublishFn = (
  startupId: string,
  fields: Record<string, string>,
) => Promise<{ error: unknown }>;

export interface DigestConfirmResult {
  /** The exact allowlisted payload that was (attempted to be) published. */
  published: Record<string, string>;
  /** Any error the publish write returned (null on success / no-op). */
  error: unknown;
}

/**
 * Orchestrate a review confirm: publish the approved fields, then run the
 * rescore path — the two are decoupled.
 *
 * - Computes the payload via {@link selectPublishFields} (toggled ∩ allowlist).
 * - Issues the publish write ONLY when something survives the filter (no empty
 *   write). If nothing is approved, publish is skipped entirely.
 * - Calls `onConfirmed` (the rescore path) on a clean publish — regardless of
 *   how many fields were toggled, INCLUDING none. Publish failure is surfaced to
 *   the caller (via `error`) and does NOT fire `onConfirmed`, so the founder can
 *   retry the publish (or explicitly skip) rather than silently losing the write.
 *
 * The publish seam is injected so this is testable without the DB or a renderer.
 */
export async function runDigestConfirm(params: {
  startupId: string;
  fieldStates: DigestFieldState[];
  publish: PublishFn;
  onConfirmed: () => void;
}): Promise<DigestConfirmResult> {
  const published = selectPublishFields(params.fieldStates);

  let error: unknown = null;
  if (Object.keys(published).length > 0) {
    const res = await params.publish(params.startupId, published);
    error = res.error;
  }

  if (!error) {
    params.onConfirmed();
  }

  return { published, error };
}
